import { type FC, useCallback, useContext, useEffect } from "react";
import { Banner, BannerType } from "@stubhub/broadway-web";
import { useSimpleFeatureTernary } from "@viagogo/feature";
import { useContent } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { LocationContext } from "src/shared/context/location-context";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import { useIsCalendarView } from "src/website-apps/shared/hooks/useIsCalendarView";
import { useIsMlb } from "src/website-apps/shared/hooks/useIsMlb";
import { DemandTrendBanner } from "./demand-trend-banner/demand-trend-banner";
import { PeopleViewedBanner } from "./PeopleViewedBanner";
import { PriceTrendBanner } from "./price-trend-banner/price-trend-banner";
import { infoBannersRecipe } from "./styled-components";
import { TicketsPurchasedBanner } from "./TicketsPurchasedBanner";

interface Props {
  noTopSpacing?: boolean;
}

export const InfoBanners: FC<Props> = ({ noTopSpacing = false }) => {
  const storePageEvent = useStorePageEvent();
  const model = usePerformerPageModelData();
  const [userLocation] = useContext(LocationContext);

  const { isOnCalendarView } = useIsCalendarView();
  const isMlb = useIsMlb();

  /* --- THEATER WEEK PROMO BANNER --- */
  /** "Buy Theatre Week tickets Dec 15–22 and get $50 coupon for future Broadway shows." */
  const theaterWeekContent = useContent("cid$#4072403#");
  const THEATER_WEEK_CATEGORY_ID = 150_596_990;
  const canParticipateTheaterWeekPromo = useCallback(
    () => model.categorySummary.categoryId === THEATER_WEEK_CATEGORY_ID,
    [model.categorySummary.categoryId],
  );
  const theaterWeekPromoFeature = useSimpleFeatureTernary(
    FeatureSetEnum.TMPerformerTheaterWeekBannerKillswitch,
    false,
    true,
    canParticipateTheaterWeekPromo,
  );

  /* --- DEMAND TREND BANNER --- */
  const canParticipateDemandTrendBanner = useCallback(() => !!model.demandTrendData, [model]);
  const demandTrendData = useSimpleFeatureTernary(
    FeatureSetEnum.TMPerformerDemandTrendBanner,
    null,
    model.demandTrendData,
    canParticipateDemandTrendBanner,
  );
  const showDemandTrendBanner = !!demandTrendData;

  /* --- PRICE TREND BANNER --- */
  const showPriceTrendBanner = !!model.priceTrendData && !!userLocation?.n;

  /* --- TICKETS PURCHASED BANNER --- */
  const canParticipateTicketsPurchasedBanner = useCallback(
    () => !!model.ticketsPurchasedLast24Hours && !showDemandTrendBanner && !showPriceTrendBanner,
    [model.ticketsPurchasedLast24Hours, showDemandTrendBanner, showPriceTrendBanner],
  );
  const ticketsPurchasedCount = useSimpleFeatureTernary(
    FeatureSetEnum.TMPerformerTicketsPurchasedBannerBlue,
    null,
    model.ticketsPurchasedLast24Hours,
    canParticipateTicketsPurchasedBanner,
  );
  const showTicketsPurchasedBanner = !!ticketsPurchasedCount;

  /* --- NUMBER OF PEOPLE VIEWED BANNER --- */
  const peopleViewedBannerEligible =
    !!model.numViewersMessage?.hasValue &&
    !showTicketsPurchasedBanner &&
    !showDemandTrendBanner &&
    !showPriceTrendBanner;
  const canParticipateNumberPeopleViewedBanner = useCallback(
    () => isMlb && peopleViewedBannerEligible,
    [isMlb, peopleViewedBannerEligible],
  );
  const showNumberPeopleViewedBanner = useSimpleFeatureTernary(
    FeatureSetEnum.TPerformerSocialProofBannerRemoval,
    peopleViewedBannerEligible,
    false,
    canParticipateNumberPeopleViewedBanner,
  );

  useEffect(() => {
    if (showTicketsPurchasedBanner || showNumberPeopleViewedBanner || showDemandTrendBanner || showPriceTrendBanner) {
      storePageEvent(
        PE_TYPE.LOGGING,
        PE_KEYS.PERFORMER_INFO_BANNERS_SHOWN,
        `Tickets Purchased Visible: ${String(showTicketsPurchasedBanner)}, Num Viewers Visible: ${String(showNumberPeopleViewedBanner)}, Demand Trend Visible: ${String(showDemandTrendBanner)}, Price Trend Visible: ${String(showPriceTrendBanner)}`,
      );
    }
  }, [
    showTicketsPurchasedBanner,
    showNumberPeopleViewedBanner,
    showDemandTrendBanner,
    showPriceTrendBanner,
    storePageEvent,
  ]);

  const hasBannersToShow =
    !!theaterWeekPromoFeature ||
    !!showDemandTrendBanner ||
    !!showPriceTrendBanner ||
    !!showTicketsPurchasedBanner ||
    !!showNumberPeopleViewedBanner;

  if (!hasBannersToShow) {
    return null;
  }

  const bannerStyles = infoBannersRecipe({
    topSpacing: noTopSpacing ? "none" : "default",
    childMargin: isOnCalendarView ? "none" : "default",
  });

  return (
    <div className={bannerStyles.wrapper}>
      <div className={bannerStyles.row}>
        <div className={bannerStyles.col}>
          {theaterWeekPromoFeature ? (
            <Banner type={BannerType.Info}>{theaterWeekContent}</Banner>
          ) : (
            <>
              {showDemandTrendBanner && <DemandTrendBanner demandTrendData={demandTrendData} />}
              {model.priceTrendData && userLocation?.n && (
                <PriceTrendBanner priceTrendData={model.priceTrendData} locationName={userLocation.n} />
              )}
              {showTicketsPurchasedBanner && <TicketsPurchasedBanner count={ticketsPurchasedCount} />}
              {showNumberPeopleViewedBanner && !!model.numViewersMessage && (
                <PeopleViewedBanner numberOfPeopleViewedMessage={model.numViewersMessage} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
